import { Link } from "react-router-dom";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { SeoHead } from "@/components/seo/SeoHead";
import { StructuredData } from "@/components/seo/StructuredData";
import {
  getBaseUrl,
  buildBreadcrumbSchema,
  buildOrganizationSchema,
  buildSoftwareApplicationSchema,
  buildWebPageSchema,
} from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { Home, MessageCircle, Calendar, ArrowRight } from "lucide-react";

const TITLE = "Agente de IA para imobiliárias no WhatsApp e Instagram | FabChat";
const DESCRIPTION =
  "Agente de IA para imobiliárias no WhatsApp e Instagram: qualificação de leads, agendamento de visitas e follow-up comercial com CRM integrado.";

const BREADCRUMB = [
  { name: "Início", path: "/" },
  { name: "Imobiliárias", path: "/setores/imobiliarias" },
];

export default function SetoresImobiliarias() {
  const baseUrl = getBaseUrl();
  const breadcrumbSchema = buildBreadcrumbSchema(baseUrl, BREADCRUMB);
  const organizationSchema = buildOrganizationSchema(baseUrl);
  const softwareSchema = buildSoftwareApplicationSchema(baseUrl, DESCRIPTION);
  const webPageSchema = buildWebPageSchema(baseUrl, { path: "/setores/imobiliarias", title: TITLE, description: DESCRIPTION });

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SeoHead title={TITLE} description={DESCRIPTION} canonicalPath="/setores/imobiliarias" />
      <StructuredData data={[organizationSchema, softwareSchema, breadcrumbSchema, webPageSchema]} />
      <Header />

      <main className="flex-1">
        <section className="container mx-auto px-4 py-16 md:py-24 max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Agente de IA para imobiliárias</h1>
          <p className="text-lg text-muted-foreground mb-12">
            Atendimento automatizado no WhatsApp e Instagram para corretores e imobiliárias: informações, visitas e follow-up.
          </p>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Por que usar a FabChat no setor imobiliário</h2>
            <p className="text-muted-foreground mb-4">
              Imobiliárias e corretores recebem muitas mensagens por WhatsApp e Instagram pedindo informações sobre imóveis, bairros e visitas. Com um <Link to="/whatsapp/agente-ia" className="text-primary hover:underline">agente de IA</Link> configurado na FabChat, é possível responder dúvidas iniciais, informar sobre disponibilidade e agendar visitas (com integração a calendário). O lead fica registrado no CRM da plataforma, com histórico de conversa e estágio no funil, e a equipe pode fazer follow-up no momento certo sem perder o contato.
            </p>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">Recursos que se encaixam no dia a dia</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li className="flex gap-3">
                <MessageCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Respostas automáticas a perguntas sobre imóveis, localização e condições, no tom da imobiliária.</span>
              </li>
              <li className="flex gap-3">
                <Calendar className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Agendamento de visitas: integração com Google Calendar para verificar disponibilidade e criar eventos.</span>
              </li>
              <li className="flex gap-3">
                <Home className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span>Follow-up inteligente: manter o lead aquecido com mensagens no timing certo, conforme configurado.</span>
              </li>
            </ul>
          </section>

          <section className="mb-10">
            <h2 className="text-2xl font-semibold mb-4">CRM e funil para corretores</h2>
            <p className="text-muted-foreground mb-4">
              Os contatos que chegam por WhatsApp ou Instagram viram leads no <Link to="/integracoes/crm" className="text-primary hover:underline">CRM integrado da plataforma</Link>. Você organiza por estágios (ex.: primeiro contato, visitou, proposta enviada) e acompanha o histórico de conversas no mesmo painel. Assim a equipe sabe onde cada lead está e pode priorizar quem está mais quente.
            </p>
          </section>

          <p className="text-sm text-muted-foreground mb-6">
            Para operação por canal, veja também <Link to="/whatsapp/agente-ia" className="text-primary hover:underline">agente de IA para WhatsApp</Link> e <Link to="/instagram/agente-ia" className="text-primary hover:underline">agente de IA para Instagram</Link>.
          </p>

          <section className="flex flex-col sm:flex-row gap-4 pt-6">
            <Button asChild size="lg" className="gap-2">
              <Link to="/planos">Ver planos <ArrowRight className="h-4 w-4" /></Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/contato">Falar com a gente</Link>
            </Button>
          </section>
        </section>
      </main>

      <Footer />
    </div>
  );
}
